/**
 * Companies Table Component
 *
 * Main table component displaying all companies
 */

import React from 'react';
import type { Company } from '../types/company';
import type { CabinetSelfListEntry } from '../types/company';
import { __ } from '@/hub-shared/utils/i18n';
import CompanyRow from './CompanyRow';
import CabinetSelfRow from './CabinetSelfRow';
import { LoadingState, TablePagination } from '@tpct/admin-ui';
import CompaniesEmptyState from './CompaniesEmptyState';

type CompaniesTableProps = {
  companies: Company[];
  cabinetSelfEntry?: CabinetSelfListEntry | null;
  loading: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onRefresh: () => void;
  onDelete: (companyId: number) => void;
  navigateTo?: (path: string) => void;
  bootstrapKey?: string;
};

const CompaniesTable = ({
  companies,
  cabinetSelfEntry = null,
  loading,
  page,
  totalPages,
  onPageChange,
  onRefresh,
  onDelete,
  navigateTo,
  bootstrapKey,
}: CompaniesTableProps) => {
  if (loading) {
    return (
      <LoadingState
        columns={[__('Nom'), __('SIREN'), __('Utilisateurs'), __('Créé'), __('Actions')]}
        className="companies-loading"
      />
    );
  }

  if (companies.length === 0 && !(page === 1 && cabinetSelfEntry)) {
    return <CompaniesEmptyState onRefresh={onRefresh} />;
  }

  return (
    <div className="container-style">
      <table className="thn-table companies-table">
        <thead>
          <tr>
            <th scope="col">{__('Nom')}</th>
            <th scope="col">{__('SIREN')}</th>
            <th scope="col">{__('Utilisateurs')}</th>
            <th scope="col">{__('Créé')}</th>
            <th scope="col">{__('Actions')}</th>
          </tr>
        </thead>
        <tbody>
          {page === 1 && cabinetSelfEntry ? (
            <CabinetSelfRow
              key={`cabinet-self-${cabinetSelfEntry.cabinet_id}`}
              entry={cabinetSelfEntry}
              navigateTo={navigateTo}
              bootstrapKey={bootstrapKey}
            />
          ) : null}
          {companies.map((company) => (
            <CompanyRow
              key={company.id}
              company={company}
              onDelete={onDelete}
              navigateTo={navigateTo}
              bootstrapKey={bootstrapKey}
            />
          ))}
        </tbody>
      </table>
      <TablePagination
        page={page - 1}
        totalPages={totalPages}
        onPrev={() => onPageChange(Math.max(1, page - 1))}
        onNext={() => onPageChange(Math.min(totalPages, page + 1))}
        previousPageAriaLabel={__('Page précédente')}
        nextPageAriaLabel={__('Page suivante')}
      />
    </div>
  );
};

export default CompaniesTable;

